import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import gsap from "gsap";
import Particles from "react-tsparticles";
import { loadFull } from "tsparticles";

export const YOUR_WHATSAPP_NUMBER = "+917304660050";

const HeroSection = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => setIsVisible(true), []);

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl.from(".hero-badge", { y: 30, opacity: 0, duration: 0.6 })
        .from(".hero-title", { y: 40, opacity: 0, duration: 0.8 }, "-=0.3")
        .from(".hero-para", { y: 20, opacity: 0, duration: 0.6 }, "-=0.4")
        .from(
          ".hero-ctas > *",
          { y: 20, opacity: 0, duration: 0.6, stagger: 0.12 },
          "-=0.4"
        )
        .from(
          ".hero-stat",
          { y: 30, opacity: 0, duration: 0.6, stagger: 0.12 },
          "-=0.5"
        );

      gsap.to(".hero-blob-left", {
        yPercent: -20,
        ease: "none",
        scrollTrigger: { scrub: true },
      });
      gsap.to(".hero-blob-right", {
        yPercent: 20,
        ease: "none",
        scrollTrigger: { scrub: true },
      });
    });

    return () => ctx.revert();
  }, []);

  const handleVideoError = () => {
    setVideoError(true);
    setVideoLoading(false);
  };

  const handleVideoLoaded = () => {
    setVideoLoading(false);
    videoRef.current?.play().catch(() => {});
  };

  const particlesInit = async (engine) => {
    try {
      await loadFull(engine);
    } catch {}
  };

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const particlesOptions = {
    fpsLimit: 60,
    particles: {
      number: { value: 26, density: { enable: true, area: 900 } },
      color: { value: "#ffffff" },
      opacity: { value: 0.08, random: true },
      size: { value: { min: 1, max: 4 } },
      move: { enable: true, speed: 0.6, outMode: "bounce" },
    },
    detectRetina: true,
  };

  return (
    <section className="hero-section min-h-[100svh]">
      {/* Video */}
      <div className="hero-video-container">
        {!prefersReduced && !videoError && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onError={handleVideoError}
            onLoadedData={handleVideoLoaded}
            className="hero-video"
            aria-hidden
          >
            <source src="/assets/images/Hero_Section.mp4" type="video/mp4" />
            <source src="/assets/images/Hero_Section.webm" type="video/webm" />
          </video>
        )}
        <div className="hero-video-overlay" />
      </div>

      {/* Fallbacks */}
      {(prefersReduced || videoError) && (
        <div className="absolute inset-0 -z-30 bg-gradient-to-br from-indigo-900/40 via-purple-900/30 to-pink-900/40" />
      )}

      {/* Blobs */}
      <div className="hero-blob-left" />
      <div className="hero-blob-right" />

      {/* Particles (disabled on mobile) */}
      {!prefersReduced && !videoError && (
        <div className="hero-particles hidden md:block">
          <Particles init={particlesInit} options={particlesOptions} />
        </div>
      )}

      {/* Content */}
      <div className="hero-content pt-25 md:pt-45 px-5">
        <div
          className={`transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-2 md:px-6 md:py-3 rounded-full mb-6 border border-white/30 hero-badge">
            <Icon name="Award" size={18} color="#fff" />
            <span className="text-white text-xs md:text-sm font-medium">
              13+ Years of IT & Branding Excellence
            </span>
          </div>

          {/* Heading */}
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-snug hero-title">
            Where IT Meets Branding
            <br />
            <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
              Driving Technology & Practical
            </span>
          </h1>

          {/* Paragraph */}
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-8 max-w-3xl mx-auto hero-para">
            Growing Indian startup delivering scalable SaaS, IT, Cybersecurity,
            Healthcare, Technology & Physical Marketing solutions.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 mb-10 hero-ctas">
            <Button
  size="lg"
  onClick={() => navigate("/services")}
  iconName="ArrowRight"
  iconPosition="right"
  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg 
             w-half sm:w-auto py-3 text-base"
>
  Explore Services
</Button>


            <Button
  variant="outline"
  size="lg"
  onClick={() => {
    const message = "Hello! I need free consultation.";
    window.open(
      `https://wa.me/${YOUR_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  }}
  className="border-2 border-white text-white hover:bg-white/10 
             w-half sm:w-auto py-3 text-base flex items-center justify-center gap-2"
>
  <img src="/assets/whatsapp.svg" alt="WhatsApp" className="w-5 h-5" />
  Get Free Consultation on WhatsApp
</Button>

          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { icon: "Users", value: "196+", label: "Happy Clients" },
              { icon: "Briefcase", value: "223+", label: "Projects Delivered" },
              { icon: "Calendar", value: "13K+", label: "Client Meetings" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hero-stat"
              >
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name={stat.icon} size={22} color="#fff" />
                </div>
                <div className="text-3xl font-bold text-white">
                  {stat.value}
                </div>
                <div className="text-sm text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
